import React from 'react';
import { useLocation } from 'react-router-dom';
import PageLayout from 'components/PageLayout';
import LoadingState from 'components/LoadingState';
import ErrorState from 'components/ErrorState';
import QualificationSubmissionForm from 'components/QualificationSubmissionForm';
import { useQualificationForm } from 'hooks/useQualificationForm';
import { useAuth } from 'contexts/AuthContext';
import AccessDenied from 'components/AccessDenied';
import NotAuthenticated from 'components/NotAuthenticated';
import { useRoleInfo } from 'hooks/useRoleInfo';

const QualificationFormPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const { state } = useLocation();
  const editSubmission = state?.editSubmission ?? null;

  const {
    userRoleType,
    userPillar,
    userIsPillarPIC,
    userIsCategoryJudge,
    userIsSuperAdmin,
  } = useRoleInfo();

  // Determine access control - only PIC_PILLAR can submit
  const userHasAccess = userIsPillarPIC || userIsSuperAdmin; // Super admins can access for oversight but not submit

  const {
    selectedCategory,
    submitted,
    error,
    isSubmitting,
    processingStage,
    categories,
    handleCategoryChange,
    handleFormSubmit,
    dynamicFormSchema,
    progressStep,
    isEditMode,
    initialValues,
  } = useQualificationForm(editSubmission);

  // If user is not authenticated, show login prompt
  if (!isAuthenticated()) {
    return (
      <PageLayout
        title="Qualification Stage Submission"
        subtitle="Authentication required to submit qualification forms."
      >
        <NotAuthenticated
          message="Please log in to access the qualification form submission."
          buttonLink="/login"
        />
      </PageLayout>
    );
  }

  // If user doesn't have access, show access denied message
  if (!userHasAccess) {
    return (
      <PageLayout
        title="Qualification Stage Submission"
        subtitle="Access restricted to Pillar PICs only."
      >
        <AccessDenied
          message={
            <>
              Only Pillar PICs can submit qualification forms.
              {userIsCategoryJudge
                ? ' Category judges can evaluate submissions but cannot submit new ones.'
                : userIsSuperAdmin
                  ? ' Award administrators no longer have access to submit qualification forms.'
                  : ' Please contact your administrator if you need access.'}
            </>
          }
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="Qualification Stage Submission"
      subtitle={
        isEditMode
          ? 'Update your SMDD qualification submission.'
          : 'Complete this qualification form to begin your SMDD entry.'
      }
    >
      {/* Progress Wizard - Transparent on gray background */}
      <div className="flex justify-center items-center space-x-8 py-6 mb-8">
        <div className="flex items-center">
          <div
            className={`w-10 h-10 ${
              progressStep >= 1
                ? 'bg-black text-white'
                : 'bg-gray-300 text-gray-600'
            } rounded-full flex items-center justify-center text-sm font-bold transition-all`}
          >
            1
          </div>
          <span
            className={`ml-3 text-sm ${
              progressStep >= 1 ? 'text-gray-900' : 'text-gray-500'
            } font-baijamjuree font-medium`}
          >
            Category
          </span>
        </div>
        <div className="w-12 h-px bg-gray-300"></div>
        <div className="flex items-center">
          <div
            className={`w-10 h-10 ${
              progressStep >= 2
                ? 'bg-black text-white'
                : 'bg-gray-300 text-gray-600'
            } rounded-full flex items-center justify-center text-sm font-bold transition-all`}
          >
            2
          </div>
          <span
            className={`ml-3 text-sm ${
              progressStep >= 2 ? 'text-gray-900' : 'text-gray-500'
            } font-baijamjuree font-medium`}
          >
            Details
          </span>
        </div>
        <div className="w-12 h-px bg-gray-300"></div>
        <div className="flex items-center">
          <div
            className={`w-10 h-10 ${
              progressStep >= 3
                ? 'bg-black text-white'
                : 'bg-gray-300 text-gray-600'
            } rounded-full flex items-center justify-center text-sm font-bold transition-all`}
          >
            3
          </div>
          <span
            className={`ml-3 text-sm ${
              progressStep >= 3 ? 'text-gray-900' : 'text-gray-500'
            } font-baijamjuree font-medium`}
          >
            Submit
          </span>
        </div>
      </div>

      {/* Main Content */}
      {isSubmitting ? (
        <div className="flex items-center justify-center min-h-[400px]">
          <LoadingState
            message={processingStage || 'Processing your submission...'}
          />
        </div>
      ) : error ? (
        <div className="flex items-center justify-center min-h-[400px]">
          <ErrorState
            title="Submission Error"
            error={error}
            onRetry={handleFormSubmit}
          />
        </div>
      ) : (
        <QualificationSubmissionForm
          selectedCategory={selectedCategory}
          submitted={submitted}
          error={error}
          isSubmitting={isSubmitting}
          categories={categories}
          handleCategoryChange={handleCategoryChange}
          handleFormSubmit={handleFormSubmit}
          dynamicFormSchema={dynamicFormSchema}
          userRoleType={userRoleType}
          userPillar={userPillar}
          initialValues={initialValues}
          isEditMode={isEditMode}
        />
      )}
    </PageLayout>
  );
};

export default QualificationFormPage;
